/**
 * AppLauncher Component
 * Odoo-style full-screen app launcher, organized by domain groups.
 * Features:
 *  - Live search across all items
 *  - Domain color coding
 *  - Keyboard navigation (ESC to close, Enter to navigate)
 *  - Persists last-used items in localStorage
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  MagnifyingGlassIcon,
  XMarkIcon,
  ChartBarIcon,
  MapPinIcon,
  CubeIcon,
  ServerStackIcon,
  SwatchIcon,
  GlobeAltIcon,
  ComputerDesktopIcon,
  RectangleStackIcon,
  LockClosedIcon,
  WrenchScrewdriverIcon,
  DocumentTextIcon,
  Cog6ToothIcon,
  ShieldCheckIcon,
  CpuChipIcon,
  WifiIcon,
  ArrowsRightLeftIcon,
  BoltIcon,
  MapIcon,
  CircleStackIcon,
  CloudArrowDownIcon,
  ClockIcon,
  KeyIcon,
  PrinterIcon,
  PhoneIcon,
  LinkIcon,
  SignalIcon,
  PuzzlePieceIcon,
  Squares2X2Icon,
  TableCellsIcon,
} from '@heroicons/react/24/outline';
import { DOMAIN_GROUPS, ALL_ITEMS } from '../menuConfig';
import { useTranslation } from 'react-i18next';
import './AppLauncher.css';

// Icon map - matches Sidebar iconMap
const ICON_MAP = {
  dashboard:            ChartBarIcon,
  locations:            MapPinIcon,
  racks:                CubeIcon,
  servers:              ComputerDesktopIcon,
  chassis:              ServerStackIcon,
  storage:              RectangleStackIcon,
  switches:             ArrowsRightLeftIcon,
  routers:              GlobeAltIcon,
  firewalls:            ShieldCheckIcon,
  pdus:                 Cog6ToothIcon,
  'patch-panels':       TableCellsIcon,
  cabling:              LinkIcon,
  workstations:         ComputerDesktopIcon,
  'ip-phones':          PhoneIcon,
  printers:             PrinterIcon,
  peripherals:          PuzzlePieceIcon,
  'networks-dashboard': ChartBarIcon,
  'networks-blocks':    RectangleStackIcon,
  'networks-subnets':   Squares2X2Icon,
  'networks-explorer':  MagnifyingGlassIcon,
  'networks-assign':    LinkIcon,
  'networks-vlans':     TableCellsIcon,
  'networks-ips':       ServerStackIcon,
  'networks-vips':      ShieldCheckIcon,
  'networks-dhcp':      BoltIcon,
  'wifi-dashboard':     WifiIcon,
  'wifi-topology':      MapIcon,
  'wifi-controllers':   WifiIcon,
  'wifi-aps':           WifiIcon,
  'wifi-radius':        LockClosedIcon,
  'wan-operators':      ArrowsRightLeftIcon,
  'wan-circuits':       LinkIcon,
  'wan-routers':        SignalIcon,
  'virt-overview':      ChartBarIcon,
  'virt-topology':      MapIcon,
  'virt-hypervisors':   CpuChipIcon,
  'virt-clusters':      CubeIcon,
  'virt-datastores':    RectangleStackIcon,
  'virt-vswitches':     SwatchIcon,
  'virt-vms':           ComputerDesktopIcon,
  'services-list':      WrenchScrewdriverIcon,
  'services-graph':     MapIcon,
  'services-depmap':    MapIcon,
  'service-farms':      ServerStackIcon,
  'backup-policies':    CloudArrowDownIcon,
  'db-instances':       CircleStackIcon,
  'licenses':           KeyIcon,
  'web-sites':          GlobeAltIcon,
  'feedback-hub':       DocumentTextIcon,
  'power-canvas':       BoltIcon,
  'network-canvas':     CpuChipIcon,
  'network-topology':   MapIcon,
  'datacenter-diagram': MapIcon,
  roles:                ShieldCheckIcon,
  naming:               DocumentTextIcon,
  setup:                Cog6ToothIcon,
  'agent-settings':     CpuChipIcon,
  'agent-mapping':      MapIcon,
};

const RECENT_KEY = 'launcher_recent_items';
const MAX_RECENT = 6;

function getRecentItems() {
  try {
    const raw = localStorage.getItem(RECENT_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function addRecentItem(item) {
  try {
    const recent = getRecentItems().filter(r => r.key !== item.key);
    recent.unshift({ key: item.key, label: item.label, path: item.path });
    localStorage.setItem(RECENT_KEY, JSON.stringify(recent.slice(0, MAX_RECENT)));
  } catch {
    // ignore
  }
}

const ItemIcon = ({ itemKey, className = '' }) => {
  const IconComponent = ICON_MAP[itemKey];
  if (IconComponent) return <IconComponent className={`launcher-item-icon ${className}`} />;
  return <CubeIcon className={`launcher-item-icon ${className}`} />;
};

const AppLauncher = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [search, setSearch] = useState('');
  const [recentItems, setRecentItems] = useState([]);
  const inputRef = useRef(null);

  // Load recents on open
  useEffect(() => {
    if (isOpen) {
      setSearch('');
      setRecentItems(getRecentItems());
      // Autofocus search
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [isOpen]);

  // ESC to close
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  // Close on route change
  useEffect(() => {
    if (isOpen) onClose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const handleNavigate = useCallback((item) => {
    addRecentItem(item);
    navigate(item.path);
    onClose();
  }, [navigate, onClose]);

  // Filter logic
  const query = search.trim().toLowerCase();

  const filteredGroups = query
    ? null // show flat search results when querying
    : DOMAIN_GROUPS;

  const searchResults = query
    ? ALL_ITEMS.filter(item =>
        t(`menu.items.${item.key}`, item.label).toLowerCase().includes(query) ||
        t(`menu.groups.${item.domainKey}`, item.domainLabel).toLowerCase().includes(query)
      )
    : null;

  const recentFiltered = recentItems
    .map(r => ALL_ITEMS.find(i => i.key === r.key))
    .filter(Boolean);

  return (
    <div
      className={`app-launcher-overlay ${isOpen ? 'open' : ''}`}
      role="dialog"
      aria-modal="true"
      aria-label={t('menu.launcher.title', 'Selector de módulos')}
    >
      {/* Backdrop */}
      <div className="app-launcher-backdrop" onClick={onClose} />

      {/* Panel */}
      <div className="app-launcher-panel">

        {/* Header */}
        <div className="launcher-header">
          <div className="launcher-search-wrap">
            <MagnifyingGlassIcon className="launcher-search-icon" />
            <input
              ref={inputRef}
              type="search"
              className="launcher-search-input"
              placeholder={t('menu.launcher.search', 'Buscar módulo...')}
              value={search}
              onChange={e => setSearch(e.target.value)}
              autoComplete="off"
            />
            {search && (
              <button className="launcher-search-clear" onClick={() => setSearch('')} aria-label="Limpiar">
                <XMarkIcon />
              </button>
            )}
          </div>
          <button className="launcher-close-btn" onClick={onClose} aria-label="Cerrar">
            <XMarkIcon />
          </button>
        </div>

        {/* Body */}
        <div className="launcher-body">

          {/* Search Results */}
          {searchResults && (
            <div className="launcher-section">
              <div className="launcher-section-label">
                {searchResults.length} resultado{searchResults.length !== 1 ? 's' : ''}
              </div>
              <div className="launcher-grid">
                {searchResults.map(item => (
                  <LauncherItem key={item.key} item={item} onClick={handleNavigate} />
                ))}
                {searchResults.length === 0 && (
                  <p className="launcher-no-results">Sin resultados para &ldquo;{search}&rdquo;</p>
                )}
              </div>
            </div>
          )}

          {/* Default view: recents + all groups */}
          {!searchResults && (
            <>
              {/* Recent Items */}
              {recentFiltered.length > 0 && (
                <div className="launcher-section">
                  <div className="launcher-section-label recents">
                    <ClockIcon className="section-label-icon" />
                    {t('menu.launcher.recent', 'Recientes')}
                  </div>
                  <div className="launcher-grid">
                    {recentFiltered.map(item => (
                      <LauncherItem key={item.key} item={item} onClick={handleNavigate} isRecent />
                    ))}
                  </div>
                </div>
              )}

              {/* Dashboard standalone */}
              <div className="launcher-section">
                <div className="launcher-section-label">{t('menu.launcher.general', 'PANEL GENERAL')}</div>
                <div className="launcher-grid">
                  <LauncherItem
                    item={{ key: 'dashboard', label: 'Dashboard', path: '/dashboard', domainColor: '#64748b', domainLabel: 'Panel general', implemented: true }}
                    onClick={handleNavigate}
                  />
                </div>
              </div>

              {/* Domain Groups */}
              {DOMAIN_GROUPS.map(group => (
                <div key={group.key} className="launcher-section">
                  <div
                    className="launcher-section-label"
                    style={{ '--domain-color': group.color }}
                  >
                    <span className="section-label-dot" />
                    {t(`menu.groups.${group.key}`, group.label).toUpperCase()}
                  </div>
                  <div className="launcher-grid">
                    {group.items.map(item => (
                      <LauncherItem
                        key={item.key}
                        item={{ ...item, domainColor: group.color, domainLabel: group.label }}
                        onClick={handleNavigate}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

/** Single item card in the launcher grid */
const LauncherItem = ({ item, onClick, isRecent = false }) => {
  const { t } = useTranslation();
  const color = item.domainColor || '#64748b';
  const label = t(`menu.items.${item.key}`, item.label);
  return (
    <button
      className={`launcher-item ${!item.implemented ? 'not-implemented' : ''} ${isRecent ? 'is-recent' : ''}`}
      onClick={() => onClick(item)}
      title={!item.implemented ? t('menu.underConstruction', '🚧 En construcción') : label}
      style={{ '--item-color': color }}
    >
      <div className="launcher-item-icon-wrap">
        <ItemIcon itemKey={item.key} />
      </div>
      <span className="launcher-item-label">{label}</span>
      {!item.implemented && <span className="launcher-item-badge">🚧</span>}
    </button>
  );
};

export default AppLauncher;
